import { Component, Event, EventEmitter, Host, h, Prop, Element, State } from '@stencil/core';

@Component({
  tag: 'spw-header-navigation-item',
  styleUrl: 'spw-header-navigation-item.scss',
  shadow: true,
})
export class SpwHeaderNavigationItem {
  @Element() el: HTMLSpwHeaderNavigationItemElement;

  /** Définit si l'élément de menu est la page courante */
  @Prop() isActive: boolean = false;

  @State() internalIsActive: boolean = false;
  @State() hasSubnav: boolean = false;
  @State() level: number = 1;
  @State() isSubnavVisible: boolean = false;
  @State() lastClickedLevel1Item: string | null = null;

  @Event() spwHeaderNavigationNavigate: EventEmitter<{ level: number; element: HTMLSpwHeaderNavigationItemElement }>;

  componentWillLoad() {
    this.internalIsActive = this.isActive;
    this.checkForSubnav();
    this.calculateLevel();
    this.updateLevelClass();
  }

  componentWillUpdate() {
    if (this.isActive !== this.internalIsActive) {
      this.internalIsActive = this.isActive;
    }
    this.updateLevelClass();
  }

  private checkForSubnav() {
    const slot = this.el.shadowRoot.querySelector('slot');
    if (slot) {
      const assignedElements = slot.assignedElements({ flatten: true });
      const hasSubnav = assignedElements.some(el => el.tagName === 'SPW-HEADER-NAVIGATION-DROPDOWN');

      if (hasSubnav !== this.hasSubnav) {
        this.hasSubnav = hasSubnav;
      }
    }
  }

  private calculateLevel() {
    let level = 1;
    let parent = this.el.parentElement;

    while (parent && parent.tagName !== 'SPW-HEADER-NAVIGATION') {
      if (parent.tagName === 'SPW-HEADER-NAVIGATION-DROPDOWN') {
        level++;
      }
      parent = parent.parentElement;
    }

    this.level = level;
  }

  private updateLevelClass() {
    const htmlElement = document.documentElement;
    Array.from(htmlElement.classList).forEach(className => {
      if (className.startsWith('spw-ds-nav-level-')) {
        htmlElement.classList.remove(className);
      }
    });
    htmlElement.classList.add(`spw-ds-nav-level-${this.level}`);
    this.spwHeaderNavigationNavigate.emit({ level: this.level, element: this.el });
  }

  private toggleSubnavVisibility(event: MouseEvent) {
    event.stopPropagation();

    if (this.hasSubnav && !this.isSubnavVisible) {
      const isMobileScreen = window.matchMedia('(max-width: 1024px)').matches;
      if (isMobileScreen) {
        event.preventDefault();
      }

      if (this.level === 1) {
        this.lastClickedLevel1Item = this.el.innerText;
        document.documentElement.setAttribute('data-ds-nav-level1', this.lastClickedLevel1Item);
      }

      this.isSubnavVisible = !this.isSubnavVisible;

      if (this.isSubnavVisible) {
        this.level++;
        this.updateLevelClass();
      }
    }
  }

  private getStoredLevel1Item(): string {
    return document.documentElement.getAttribute('data-ds-nav-level1') || '';
  }

  private handleBackClick(event: MouseEvent) {
    event.stopPropagation();

    this.isSubnavVisible = false;

    if (this.level > 1) {
      this.level--;
      this.updateLevelClass();
    }

    this.internalIsActive = false;

    if (this.level === 1 && !this.isSubnavVisible) {
      this.lastClickedLevel1Item = null;
      document.documentElement.removeAttribute('data-ds-nav-level1');
    }
  }

  private getPageTitle(): { title: string; link: string | null; target: string | null } | null {
    const parent = this.el.shadowRoot.querySelector('slot');

    if (parent) {
      const assignedElements = parent.assignedElements({ flatten: true }) as HTMLElement[];

      if (assignedElements.length > 0) {
        const firstElement = assignedElements[0];

        const title = firstElement.innerText || '';
        const link = (firstElement as HTMLAnchorElement).href || null;
        const target = (firstElement as HTMLAnchorElement).target || null;

        return { title, link, target };
      }
    }

    return null;
  }

  async componentDidLoad() {
    setTimeout(() => {
      this.checkForSubnav();
    }, 0);

    const slot = this.el.shadowRoot.querySelector('slot');
    if (slot) {
      const mutationObserver = new MutationObserver(() => {
        this.checkForSubnav();
      });

      mutationObserver.observe(slot, { childList: true, subtree: true });
    }
  }

  render() {
    const pageTitle = this.getPageTitle();
    const storedTitle = this.getStoredLevel1Item();

    const elementClass = {
      'spw-header-navigation-item': true,
      'spw-header-navigation-item--has-subnav': this.hasSubnav,
      'spw-header-navigation-item--is-active': this.internalIsActive,
      'spw-header-navigation-item--is-subnav-visible': this.isSubnavVisible,
    };

    return (
      <Host data-level={this.level}>
        <li class={elementClass}>
          <div class="spw-header-navigation-item__content" onClick={event => this.toggleSubnavVisibility(event)}>
            {this.isSubnavVisible && (
              <div class={`spw-header-navigation-item__top spw-header-navigation-item__top--${this.level}`}>
                <div class="spw-header-navigation-item__top-back" onClick={event => this.handleBackClick(event)}>
                  <spw-icon icon="fa-chevron-left"></spw-icon> <span>{this.level === 1 ? '' : this.level === 2 ? 'Menu principal' : storedTitle}</span>
                </div>
                <div class="spw-header-navigation-item__top-title">
                  <a href={pageTitle?.link || '#'} target={pageTitle?.target || '_self'}>
                    {pageTitle?.title || ''}
                  </a>
                </div>
              </div>
            )}
            <slot></slot>
          </div>
        </li>
      </Host>
    );
  }
}
